import React, { useMemo } from 'react';
import NavigationTabs from 'routes/Contracts/ContractDetail/navigation-tab';
import {
  constructContractDetailPath,
  constructSubmissionDetailPath,
  ENavigationPath,
  EPageFrom,
  EPageTo,
} from 'constants/path';
import { EQueryKeys } from 'constants/query-keys';
import HomeLayout from 'layouts/home-layout';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  getComponentByTabId,
  getContractConfigTabs,
} from './navigation-tab/contract-config-tabs';
import { isNumeric } from 'utils';
import ContractHeader from '../../../components/ui/contract-header';
import { contractApi } from 'services/apis';

const ContractDetail: React.FC = () => {
  const navigate = useNavigate();
  const { tabId, contractId } = useParams<{
    tabId: string;
    contractId: string;
  }>();
  const location = useLocation();
  const { from } = location.state ?? {};
  const activeKey = tabId && isNumeric(tabId) ? Number(tabId) : tabId || 0;
  const { t } = useTranslation();
  const ActiveComponent = getComponentByTabId(activeKey, t);

  const { data: contract } = useQuery(
    [EQueryKeys.CONTRACT_BY_ID, contractId, tabId],
    () => {
      return contractApi.getContractById({
        contractId: contractId as string,
      });
    },
    {
      enabled: !!contractId,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
    }
  );

  const backDestinationPath = useMemo(() => {
    if (from === EPageFrom.BINDING_DASHBOARD) {
      return constructSubmissionDetailPath(
        contract?.submission?.submissionId || '',
        '9'
      );
    }

    if (from === EPageFrom.FINANCE_DASHBOARD) {
      return ENavigationPath.DASHBOARD;
    }
    return ENavigationPath.VIEW_BINDER_CONTRACT;
  }, [from, contract?.submission?.submissionId]);

  return (
    <HomeLayout
      title="Contracts"
      backDestinationPath={backDestinationPath}
      backNavigationState={
        from === EPageFrom.FINANCE_DASHBOARD
          ? {
              to: EPageTo.FINANCE_DASHBOARD,
            }
          : undefined
      }
    >
      {contract && (
        <ContractHeader
          referenceNumber={
            contract.pinnacleReferenceNumber ??
            contract.oldPinReferenceNumber ??
            ''
          }
          title={contract.submission?.insured?.name || ''}
          contractId={contractId || ''}
          contractStatus={contract.contractStatus}
        />
      )}

      <NavigationTabs contractId={contractId} tabId={tabId} />

      {contract && (
        <div className="my-4 rounded-2xl bg-[#E3F2FB] p-3">
          <ActiveComponent
            contract={contract}
            includeBack={Number(activeKey) > 0}
            includeNext={
              Number(activeKey) < getContractConfigTabs(t).length - 1
            }
            navigateBack={() => {
              navigate(
                constructContractDetailPath(
                  contract.contractId || '',
                  String(Number(activeKey) - 1)
                ),
                { state: location.state }
              );
            }}
            navigateNext={() => {
              navigate(
                constructContractDetailPath(
                  contract.contractId || '',
                  String(Number(activeKey) + 1)
                ),
                { state: location.state }
              );
            }}
          />
        </div>
      )}
    </HomeLayout>
  );
};

export default ContractDetail;
